import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, RotateCcw, Copy, Check, Download, Tag, Zap, Braces, CircleCheck, CircleDashed, AlertTriangle,
  GitCompareArrows, Globe, Activity, ShoppingBag, MousePointerClick, Plug, ArrowUpRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Kaynak Takibi > GTM kurulumu: seçili markanın sitesine GTM'den kurulacak etiket/tetikleyici/değişkenler.
// Yalnız iç kullanım (sayfa + backend e-posta kilidi). Sayfa İngilizce.
// Backend: glowytics-backend/src/routes/attribution.js (GET/POST /setup).
const API = 'https://api.glowytics.com';
const BASE = `${API}/api/attribution`;
const SCRIPT_URL = 'https://api.glowytics.com/pixel/glowytics-attribution.js?v=5';

const PLATFORM_LABEL = { woocommerce: 'WooCommerce', ikas: 'ikas', tsoft: 'T-Soft', shopify: 'Shopify', ticimax: 'Ticimax', other: 'Other / custom' };

// Script'in dataLayer'dan kendiliğinden okuduğu GA4 olayları (glowytics-attribution.js dlEvent)
const DL_EVENTS = [
  { name: 'purchase', need: 'transaction_id, value, currency, items', use: 'Ties the order to its source', must: true },
  { name: 'begin_checkout', need: 'items', use: 'Checkout funnel' },
  { name: 'add_to_cart', need: 'items', use: 'Add to cart' },
  { name: 'view_item', need: 'items', use: 'Product view' },
  { name: 'add_shipping_info', need: 'items', use: 'Shipping step' },
  { name: 'add_payment_info', need: 'items', use: 'Payment step' },
  { name: 'view_cart', need: 'items', use: 'Cart view' },
  { name: 'view_item_list', need: 'items', use: 'List view' },
  { name: 'remove_from_cart', need: 'items', use: 'Remove from cart' },
  { name: 'search', need: 'search_term', use: 'On-site search' },
];

const SOURCE = {
  datalayer: { label: 'dataLayer', cls: 'bg-indigo-50 text-indigo-700' },
  manual: { label: 'Glowy tag', cls: 'bg-violet-50 text-violet-700' },
  adapter: { label: 'Auto', cls: 'bg-sky-50 text-sky-700' },
  jsonld: { label: 'JSON-LD', cls: 'bg-gray-100 text-gray-600' },
  server: { label: 'Plugin', cls: 'bg-amber-50 text-amber-800' },
  page_data: { label: 'Plugin', cls: 'bg-amber-50 text-amber-800' },
  legacy: { label: 'Old script', cls: 'bg-gray-100 text-gray-500' },
};

const baseHtml = (brandId, key) => `<script>window.glowyAttrConfig={brandId:'${brandId}',key:'${key}'}</script>
<script async src="${SCRIPT_URL}"></script>`;

const BRIDGE_HTML = `<script>
(function () {
  // Site's own event name -> Glowy event name. Remove lines the site doesn't use.
  var MAP = {
    'addToCart': 'add_to_cart',
    'productDetail': 'view_item',
    'checkout': 'begin_checkout',
    'orderComplete': 'purchase'
  };
  var name = MAP[{{Event}}];
  if (!name) return;
  var ec = {{Glowy - DLV ecommerce}} || {};
  var items = ec.items || ec.products || (ec.purchase && ec.purchase.products) || [];
  window.glowyAttrQ = window.glowyAttrQ || [];
  window.glowyAttrQ.push(['track', name, {
    items: items,
    value: ec.value,
    currency: ec.currency || 'TRY',
    order_id: ec.transaction_id || (ec.purchase && ec.purchase.actionField && ec.purchase.actionField.id),
    search_term: ec.search_term
  }]);
})();
</script>`;

const PURCHASE_HTML = `<script>
(function () {
  var id = {{Glowy - Order ID}};
  if (!id) return;
  window.glowyAttrQ = window.glowyAttrQ || [];
  window.glowyAttrQ.push(['track', 'purchase', {
    order_id: String(id),
    value: {{Glowy - Order Total}},
    currency: 'TRY'
  }]);
})();
</script>`;

const CONSENT_HTML = `<script>
(function () {
  // {{Glowy - Consent Granted}}: returns true when the CMP grants marketing/analytics consent
  var v = {{Glowy - Consent Granted}} ? 'granted' : 'denied';
  try { localStorage.setItem('glowy_consent', v); } catch (e) {}
  document.cookie = 'glowy_consent=' + v + '; path=/; max-age=31536000; SameSite=Lax';
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'glowy_consent_update' });
})();
</script>`;

const ECOM_REGEX = '^(addToCart|productDetail|checkout|orderComplete)$';

const fmtN = (n) => Number(n || 0).toLocaleString('en-US');
const fmtAgo = (s) => {
  if (!s) return '—';
  const m = Math.round((Date.now() - new Date(s).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};
const sumBy = (events, test) => Object.values(events).reduce((s, e) => s + Object.entries(e.by).filter(([k]) => test(k)).reduce((a, [, n]) => a + n, 0), 0);

const gtmPurchases = (h) => Object.entries(h.events.purchase?.by || {}).filter(([k]) => /datalayer|manual|adapter|jsonld/.test(k)).reduce((a, [, n]) => a + n, 0);

function buildTags(brandId, key) {
  return [
    {
      id: 'base', need: 'Required', required: true,
      name: 'Glowy Attribution - Base', type: 'Custom HTML', trigger: 'Initialization - All Pages', option: 'Once per page',
      consent: 'No additional consent required',
      code: baseHtml(brandId, key),
      gives: 'Captures the traffic source of every visit (30-day persistent + session), page views, scroll and engagement time. It also reads the GA4 e-commerce events listed in Event coverage straight from the site\'s dataLayer, so no extra tag is needed for those.',
      health: (h) => h.touches?.n > 0 || h.events.page_view?.n > 0,
      healthText: (h) => `${fmtN(h.events.page_view?.n || 0)} page views · ${fmtN(h.touches?.n || 0)} sourced visits`,
    },
    {
      id: 'bridge', need: 'If events aren\'t GA4-named',
      name: 'Glowy Attribution - Event bridge', type: 'Custom HTML', trigger: 'Glowy - Site ecommerce events', option: 'Once per event',
      consent: 'No additional consent required',
      code: BRIDGE_HTML,
      gives: 'Use when the site pushes its own event names to the dataLayer (e.g. "addToCart", "orderComplete"). Maps them to Glowy events. Fill MAP and the trigger regex with the names you see in GTM Preview.',
      warn: 'For every event routed through the bridge, add auto:{add_to_cart:false, …} to the Base tag config, otherwise it is counted twice. Purchase is safe: it is deduplicated by order ID.',
      health: (h) => sumBy(h.events, (k) => k.includes('manual')) > 0,
      healthText: (h) => `${fmtN(sumBy(h.events, (k) => k.includes('manual')))} events from Glowy tags`,
    },
    {
      id: 'purchase', need: 'If there\'s no purchase event',
      name: 'Glowy Attribution - Purchase', type: 'Custom HTML', trigger: 'Glowy - Thank-you page', option: 'Once per page',
      consent: 'No additional consent required',
      code: PURCHASE_HTML,
      gives: 'Records the order from the order ID and total on the thank-you page. Without an order ID the order cannot be tied to a source. Set up the two variables below for this site\'s thank-you page: URL, DOM element or a JS variable.',
      // eklentiden (server/page_data) gelenler sayılmaz: yalnız GTM tarafının yakaladığı purchase
      health: (h) => gtmPurchases(h) > 0,
      healthText: (h) => `${fmtN(gtmPurchases(h))} purchases captured via GTM`,
    },
    {
      id: 'consent', need: 'If the CMP isn\'t supported',
      name: 'Glowy Attribution - Consent', type: 'Custom HTML', trigger: 'Glowy - Consent updated', option: 'Once per event',
      consent: 'No additional consent required',
      code: CONSENT_HTML,
      gives: 'The script reads Cookiebot, OneTrust, CookieYes, Complianz, T-Soft and Google Consent Mode "update" by itself. If the site uses another tool, report the consent state with this tag; otherwise events are stored as "unknown".',
      health: (h) => Object.values(h.events).some((e) => e.denied > 0),
      healthText: (h) => `${fmtN(Object.values(h.events).reduce((s, e) => s + e.denied, 0))} events marked as denied`,
    },
  ];
}

const TRIGGERS = [
  { name: 'Initialization - All Pages', type: 'Built-in trigger', cond: 'No condition', for: 'Base' },
  { name: 'Glowy - Site ecommerce events', type: 'Custom Event', cond: `Event name (regex): ${ECOM_REGEX}`, for: 'Event bridge' },
  { name: 'Glowy - Thank-you page', type: 'Page View - DOM Ready', cond: 'Page Path matches regex of the order-complete page (e.g. /order-received/ · /siparis-tamamlandi · /checkout/success)', for: 'Purchase' },
  { name: 'Glowy - Consent updated', type: 'Custom Event', cond: 'The consent event the CMP pushes to the dataLayer (e.g. cookie_consent_update)', for: 'Consent' },
];
const VARIABLES = [
  { name: 'Event', type: 'Built-in variable', how: 'Variables › Configure › tick Event', for: 'Event bridge' },
  { name: 'Glowy - DLV ecommerce', type: 'Data Layer Variable', how: 'Name: ecommerce · Version 2', for: 'Event bridge' },
  { name: 'Glowy - Order ID', type: 'URL / DOM Element / JS Variable', how: 'Order number on the thank-you page', for: 'Purchase' },
  { name: 'Glowy - Order Total', type: 'DOM Element / JS Variable', how: 'Order total as a number (incl. shipping)', for: 'Purchase' },
  { name: 'Glowy - Consent Granted', type: 'Custom JavaScript', how: 'Returns true when the CMP grants marketing/analytics consent', for: 'Consent' },
];

// GTM içe aktarma dosyası (Admin > Import Container > Merge). Yalnız Base + seçilen etiketler.
function buildGtmJson(brandName, brandId, key, pick) {
  const base = { accountId: '0', containerId: '0' };
  const tpl = (k, value) => ({ type: 'TEMPLATE', key: k, value });
  const html = (id, name, code, trig, notes) => ({
    ...base, tagId: id, name, type: 'html', parameter: [tpl('html', code), { type: 'BOOLEAN', key: 'supportDocumentWrite', value: 'false' }],
    firingTriggerId: [trig], tagFiringOption: 'ONCE_PER_EVENT', monitoringMetadata: { type: 'MAP' }, consentSettings: { consentStatus: 'NOT_NEEDED' }, notes,
  });
  const tag = [{ ...html('9001', 'Glowy Attribution - Base', baseHtml(brandId, key), '2147479573', 'Glowytics first-party source/event tracking. Initialization - All Pages.'), tagFiringOption: 'ONCE_PER_LOAD' }];
  const trigger = [], variable = [], builtInVariable = [];
  if (pick.bridge) {
    tag.push(html('9002', 'Glowy Attribution - Event bridge', BRIDGE_HTML, '9101', 'Edit MAP and the trigger regex to match the site\'s event names.'));
    trigger.push({ ...base, triggerId: '9101', name: 'Glowy - Site ecommerce events', type: 'CUSTOM_EVENT',
      customEventFilter: [{ type: 'MATCH_REGEX', parameter: [tpl('arg0', '{{_event}}'), tpl('arg1', ECOM_REGEX)] }] });
    variable.push({ ...base, variableId: '9201', name: 'Glowy - DLV ecommerce', type: 'v',
      parameter: [{ type: 'INTEGER', key: 'dataLayerVersion', value: '2' }, { type: 'BOOLEAN', key: 'setDefaultValue', value: 'false' }, tpl('name', 'ecommerce')] });
    builtInVariable.push({ ...base, type: 'EVENT', name: 'Event' });
  }
  if (pick.purchase) {
    tag.push({ ...html('9003', 'Glowy Attribution - Purchase', PURCHASE_HTML, '9102', 'Adjust Glowy - Order ID / Order Total to this site\'s thank-you page.'), tagFiringOption: 'ONCE_PER_LOAD' });
    trigger.push({ ...base, triggerId: '9102', name: 'Glowy - Thank-you page', type: 'DOM_READY',
      filter: [{ type: 'MATCH_REGEX', parameter: [tpl('arg0', '{{Page Path}}'), tpl('arg1', '(order-received|siparis-tamamlandi|tesekkur|checkout/success)')] }] });
    variable.push({ ...base, variableId: '9202', name: 'Glowy - Order ID', type: 'jsm', parameter: [tpl('javascript', "function(){\n  // TODO: adjust for this site. Example: order number in the URL\n  var m = location.pathname.match(/(\\d{3,})/);\n  return m ? m[1] : undefined;\n}")] });
    variable.push({ ...base, variableId: '9203', name: 'Glowy - Order Total', type: 'jsm', parameter: [tpl('javascript', "function(){\n  // TODO: adjust for this site (element holding the order total)\n  var el = document.querySelector('.order-total .amount, .order-total');\n  if (!el) return undefined;\n  var t = el.textContent.replace(/[^0-9,.]/g, '').replace(/\\.(?=\\d{3}(\\D|$))/g, '').replace(',', '.');\n  return parseFloat(t) || undefined;\n}")] });
    builtInVariable.push({ ...base, type: 'PAGE_PATH', name: 'Page Path' });
  }
  if (pick.consent) {
    tag.push(html('9004', 'Glowy Attribution - Consent', CONSENT_HTML, '9103', 'Adjust the trigger event name and Consent Granted variable to the site\'s CMP.'));
    trigger.push({ ...base, triggerId: '9103', name: 'Glowy - Consent updated', type: 'CUSTOM_EVENT',
      customEventFilter: [{ type: 'EQUALS', parameter: [tpl('arg0', '{{_event}}'), tpl('arg1', 'cookie_consent_update')] }] });
    variable.push({ ...base, variableId: '9204', name: 'Glowy - Consent Granted', type: 'jsm', parameter: [tpl('javascript', "function(){\n  // TODO: adjust for the site's CMP\n  return false;\n}")] });
  }
  return {
    exportFormatVersion: 2, exportTime: new Date().toISOString().slice(0, 19).replace('T', ' '),
    containerVersion: {
      path: 'accounts/0/containers/0/versions/0', ...base, containerVersionId: '0',
      container: { path: 'accounts/0/containers/0', ...base, name: brandName, usageContext: ['WEB'] },
      tag, trigger, variable, builtInVariable,
    },
  };
}

// ---------------------------------------------------------------- küçük parçalar (WidgetStudio dili)
const SectionTitle = ({ icon: Icon, children, right }) => (
  <div className="flex items-center justify-between gap-2 mb-3">
    <h2 className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
      {Icon && <Icon className="w-3.5 h-3.5" />}{children}
    </h2>
    {right}
  </div>
);
const Card = ({ className = '', children }) => <div className={`bg-white border border-gray-200 rounded-xl ${className}`}>{children}</div>;

function CopyBtn({ text, label = 'Copy', small }) {
  const [ok, setOk] = useState(false);
  return (
    <button type="button" title="Copy to clipboard"
      onClick={() => { navigator.clipboard?.writeText(text).then(() => { setOk(true); setTimeout(() => setOk(false), 1500); }); }}
      className={`inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 whitespace-nowrap ${small ? 'px-1.5 py-1 text-[11px]' : 'px-2 py-1 text-xs'}`}>
      {ok ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}{label && (ok ? 'Copied' : label)}
    </button>
  );
}

const Meta = ({ k, children }) => (
  <div className="flex gap-3 py-1.5 text-xs border-b border-gray-100 last:border-0">
    <span className="w-24 flex-shrink-0 text-gray-500">{k}</span>
    <span className="text-gray-900 min-w-0 break-words">{children}</span>
  </div>
);

function Status({ on, text, configured }) {
  if (!configured) return <span className="text-[11px] text-gray-400">Not registered</span>;
  return on
    ? <span className="inline-flex items-center gap-1 text-[11px] text-green-700"><CircleCheck className="w-3.5 h-3.5" />Receiving · {text}</span>
    : <span className="inline-flex items-center gap-1 text-[11px] text-gray-500"><CircleDashed className="w-3.5 h-3.5" />No data yet</span>;
}

const Stat = ({ icon: Icon, label, value, sub, tone }) => (
  <div className="p-3 rounded-lg border border-gray-200 bg-gray-50/60 min-w-0">
    <div className="flex items-center gap-1.5 text-[11px] text-gray-500"><Icon className="w-3.5 h-3.5" />{label}</div>
    <div className={`mt-1 text-lg font-semibold tabular-nums ${tone || 'text-gray-900'}`}>{value}</div>
    {sub && <div className="text-[11px] text-gray-400 truncate">{sub}</div>}
  </div>
);

export default function AttributionSetup() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [origins, setOrigins] = useState('');
  const [platform, setPlatform] = useState('other');
  const [saving, setSaving] = useState(false);
  const [pick, setPick] = useState({ bridge: false, purchase: false, consent: false });
  const [openTag, setOpenTag] = useState('base');

  const apply = useCallback((d) => {
    setData(d);
    const o = d.site?.allowed_origins?.length ? d.site.allowed_origins : (d.brand.website ? [d.brand.website] : []);
    setOrigins(o.map((x) => x.replace(/^https?:\/\//, '').replace(/\/.*$/, '')).join(', '));
    setPlatform(d.site?.platform || 'other');
  }, []);

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true); setError('');
    try {
      const r = await fetch(`${BASE}/setup?brandId=${encodeURIComponent(brandId)}`, { credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      apply(j.data);
    } catch (e) { setError(e.message); setData(null); }
    setLoading(false);
  }, [brandId, apply]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setError('');
    try {
      const r = await fetch(`${BASE}/setup?brandId=${encodeURIComponent(brandId)}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origins: origins.split(/[\s,]+/).filter(Boolean), platform }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      apply(j.data);
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const site = data?.site;
  const health = useMemo(() => data?.health || { events: {}, orders: {}, touches: {} }, [data]);
  const tags = useMemo(() => buildTags(brandId, site?.site_key || 'KEY_NOT_CREATED'), [brandId, site?.site_key]);
  const pluginN = useMemo(() => sumBy(health.events, (k) => /server|page_data/.test(k)), [health]);
  const totalEvents = useMemo(() => Object.values(health.events).reduce((s, e) => s + e.n, 0), [health]);
  const coverage = DL_EVENTS.filter((e) => health.events[e.name]?.n > 0).length;
  const purchaseH = health.events.purchase;
  const purchasePluginOnly = purchaseH ? Object.entries(purchaseH.by).filter(([k]) => k === 'server' || k === 'page_data').reduce((a, [, n]) => a + n, 0) : 0;

  const download = () => {
    const json = buildGtmJson(data.brand.name, brandId, site.site_key, pick);
    const blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `glowy-attribution-gtm-${data.brand.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  if (!brandId) return <div className="p-8 text-sm text-gray-600">Select a brand first.</div>;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3 min-w-0">
          <GitCompareArrows className="w-5 h-5 text-indigo-600 flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-[15px] font-semibold text-gray-900">GTM Setup</h1>
            <p className="text-xs text-gray-500 truncate">{data?.brand?.name || selectedBrand?.name} · first-party source tracking via Google Tag Manager, no plugin needed</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/cro/kaynak-takibi" className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 bg-white hover:bg-gray-50">
            Source Tracking<ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
          <button onClick={load} title="Refresh" className="p-1.5 rounded-lg border border-gray-200 text-gray-500 bg-white hover:bg-gray-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
          </button>
          <button onClick={download} disabled={!site}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
            <Download className="w-3.5 h-3.5" />Export GTM container
          </button>
        </div>
      </div>

      {error && <div className="mx-4 sm:mx-6 mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</div>}
      {loading && !data && <div className="flex items-center justify-center h-[50vh] text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>}

      {data && (
        <div className="p-4 sm:p-6 grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px] items-start">
          {/* ---------------- main column ---------------- */}
          <div className="space-y-4 min-w-0 order-2 xl:order-1">
            {/* Event coverage */}
            <Card className="p-4">
              <SectionTitle icon={Zap} right={<span className="text-[11px] text-gray-400">Last 7 days · {coverage}/{DL_EVENTS.length} events receiving</span>}>Event coverage</SectionTitle>
              <p className="text-xs text-gray-500 mb-3 max-w-3xl">
                Browse the site in GTM Preview and place a test order. If these events show up with these names and fields, the Base tag is all you need.
                Different names → add the <b className="text-gray-700">Event bridge</b>. No <code className="font-mono text-[11px] bg-gray-100 px-1 rounded">purchase</code> on the thank-you page → add the <b className="text-gray-700">Purchase</b> tag.
              </p>
              <div className="overflow-x-auto -mx-4">
                <table className="w-full text-xs min-w-[680px]">
                  <thead>
                    <tr className="text-left text-[11px] text-gray-500 uppercase tracking-wide border-y border-gray-100 bg-gray-50/60">
                      <th className="py-2 pl-4 pr-2 font-semibold">Event</th>
                      <th className="py-2 px-2 font-semibold">Required fields</th>
                      <th className="py-2 px-2 font-semibold">Used for</th>
                      <th className="py-2 px-2 font-semibold text-right">Count</th>
                      <th className="py-2 px-2 font-semibold">Last seen</th>
                      <th className="py-2 pl-2 pr-4 font-semibold">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DL_EVENTS.map((e) => {
                      const h = health.events[e.name];
                      return (
                        <tr key={e.name} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/60">
                          <td className="py-2 pl-4 pr-2 whitespace-nowrap">
                            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle ${h?.n ? 'bg-green-500' : 'bg-gray-300'}`} />
                            <code className="font-mono text-[11px] text-gray-900">{e.name}</code>
                            {e.must && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-50 text-rose-600">REQUIRED</span>}
                          </td>
                          <td className="py-2 px-2 font-mono text-[11px] text-gray-500">{e.need}</td>
                          <td className="py-2 px-2 text-gray-600">{e.use}</td>
                          <td className={`py-2 px-2 text-right tabular-nums ${h?.n ? 'text-gray-900 font-medium' : 'text-gray-300'}`}>{fmtN(h?.n || 0)}</td>
                          <td className="py-2 px-2 text-gray-500 whitespace-nowrap">{h ? fmtAgo(h.last_at) : '—'}</td>
                          <td className="py-2 pl-2 pr-4">
                            <div className="flex flex-wrap gap-1">
                              {h ? Object.entries(h.by).sort((a, b) => b[1] - a[1]).map(([k, n]) => {
                                const parts = k.split('+');
                                const s = SOURCE[parts.find((p) => SOURCE[p] && p !== 'server') || parts[0]] || SOURCE.legacy;
                                const lbl = parts.map((p) => (SOURCE[p] || { label: p }).label).filter((v, i, a) => a.indexOf(v) === i).join(' + ');
                                return <span key={k} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${parts.includes('server') && parts.length === 1 ? SOURCE.server.cls : s.cls}`}>{lbl} {fmtN(n)}</span>;
                              }) : <span className="text-gray-300">—</span>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Tags */}
            <Card>
              <div className="px-4 pt-4"><SectionTitle icon={Tag} right={<span className="text-[11px] text-gray-400">{tags.length} tags</span>}>Tags to add in GTM</SectionTitle></div>
              {!site && <div className="mx-4 mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Register the site first (right panel). The key in the code fills in after that.</div>}
              <div className="divide-y divide-gray-100 border-t border-gray-100">
                {tags.map((t, i) => {
                  const open = openTag === t.id;
                  return (
                    <div key={t.id} className={open ? 'bg-white' : 'hover:bg-gray-50/60'}>
                      <button type="button" onClick={() => setOpenTag(open ? null : t.id)} className="w-full flex flex-wrap items-center gap-2 px-4 py-3 text-left">
                        <span className={`w-5 h-5 rounded-md text-[11px] font-semibold flex items-center justify-center ${t.required ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{i + 1}</span>
                        <span className="text-sm font-medium text-gray-900">{t.name}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${t.required ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>{t.need}</span>
                        <span className="ml-auto"><Status on={t.health(health)} text={t.healthText(health)} configured={!!site} /></span>
                      </button>
                      {open && (
                        <div className="px-4 pb-4 grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
                          <div>
                            <p className="text-xs text-gray-600 mb-3">{t.gives}</p>
                            <Meta k="Tag name"><span className="inline-flex items-center gap-1.5">{t.name}<CopyBtn text={t.name} label="" small /></span></Meta>
                            <Meta k="Tag type">{t.type}</Meta>
                            <Meta k="Trigger">{t.trigger}</Meta>
                            <Meta k="Firing">{t.option}</Meta>
                            <Meta k="Consent">{t.consent}</Meta>
                            {!t.required && (
                              <label className="mt-3 flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                                <input type="checkbox" className="accent-indigo-600" checked={!!pick[t.id]} onChange={(e) => setPick((p) => ({ ...p, [t.id]: e.target.checked }))} />
                                Include in export
                              </label>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[11px] text-gray-500">Custom HTML</span>
                              <CopyBtn text={t.code} label="Copy code" />
                            </div>
                            <pre className="font-mono text-[11px] leading-relaxed bg-gray-900 text-gray-100 rounded-lg p-3 overflow-x-auto whitespace-pre">{t.code}</pre>
                            {t.warn && <p className="mt-2 text-[11px] text-amber-700 flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />{t.warn}</p>}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Triggers + variables */}
            <div className="grid gap-4 lg:grid-cols-2">
              {[{ title: 'Triggers', icon: MousePointerClick, rows: TRIGGERS.map((t) => ({ name: t.name, a: t.type, b: t.cond, for: t.for })) },
                { title: 'Variables', icon: Braces, rows: VARIABLES.map((v) => ({ name: v.name, a: v.type, b: v.how, for: v.for })) }].map((blk) => (
                <Card key={blk.title} className="p-4 min-w-0">
                  <SectionTitle icon={blk.icon}>{blk.title}</SectionTitle>
                  <div className="divide-y divide-gray-100">
                    {blk.rows.map((r) => (
                      <div key={r.name} className="py-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-medium text-gray-900">{r.name}</span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-500">{r.for}</span>
                        </div>
                        <div className="text-[11px] text-gray-500 mt-0.5">{r.a} · {r.b}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>

            {/* Verify */}
            <Card className="p-4">
              <SectionTitle icon={Activity}>Import & verify</SectionTitle>
              <ol className="text-xs text-gray-600 space-y-2">
                {[
                  <>Export the container (top right) with Base + the tags ticked "Include in export". In GTM: Admin › Import Container › existing workspace › <b className="text-gray-800">Merge</b> › rename conflicting.</>,
                  <>Replace the TODOs and sample event names in the bridge / purchase / consent tags with this site's values.</>,
                  <>Open the site in GTM Preview. In the console <code className="font-mono text-[11px] bg-gray-100 px-1 rounded">glowyAttr.get()</code> shows visitor ID and source, <code className="font-mono text-[11px] bg-gray-100 px-1 rounded">glowyAttr.log</code> shows sent events.</>,
                  <>Before a test order run <code className="font-mono text-[11px] bg-gray-100 px-1 rounded">localStorage.setItem('glowy_test','1')</code>: that browser's events and order are flagged as test, kept out of reports but counted here.</>,
                  <>Publish, come back in a few hours and refresh: Event coverage and the tag statuses show whether data is arriving.</>,
                ].map((c, i) => (
                  <li key={i} className="flex gap-2.5"><span className="w-5 h-5 flex-shrink-0 rounded-full bg-gray-100 text-gray-600 text-[11px] font-semibold flex items-center justify-center">{i + 1}</span><span className="pt-0.5">{c}</span></li>
                ))}
              </ol>
            </Card>
          </div>

          {/* ---------------- side column ---------------- */}
          <div className="space-y-4 order-1 xl:order-2 xl:sticky xl:top-4">
            <Card className="p-4">
              <SectionTitle icon={Globe} right={
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${site ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'}`}>{site ? 'REGISTERED' : 'NOT REGISTERED'}</span>
              }>Site</SectionTitle>
              <p className="text-[11px] text-gray-500 mb-3">Data is accepted only from these domains with the matching key. A bare domain also adds its www version.</p>
              <label className="block text-[11px] text-gray-500 mb-1">Domains (comma separated)</label>
              <input value={origins} onChange={(e) => setOrigins(e.target.value)} placeholder="brand.com"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200" />
              <label className="block text-[11px] text-gray-500 mb-1 mt-3">Platform</label>
              <select value={platform} onChange={(e) => setPlatform(e.target.value)}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200">
                {(data.platforms || []).map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p] || p}</option>)}
              </select>
              <button onClick={save} disabled={saving || !origins.trim()}
                className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}{site ? 'Save changes' : 'Register & create key'}
              </button>
              {site && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                  {[['Brand ID', brandId], ['Site key', site.site_key]].map(([k, v]) => (
                    <div key={k} className="mb-2">
                      <div className="text-[11px] text-gray-500 mb-1">{k}</div>
                      <div className="flex items-center gap-1.5">
                        <code className="flex-1 min-w-0 truncate font-mono text-[11px] bg-gray-50 border border-gray-200 rounded-md px-2 py-1 text-gray-800">{v}</code>
                        <CopyBtn text={v} label="" small />
                      </div>
                    </div>
                  ))}
                  <div className="text-[11px] text-gray-500 mb-1 mt-3">Allowed origins</div>
                  <div className="flex flex-wrap gap-1">{site.allowed_origins.map((o) => <span key={o} className="px-1.5 py-0.5 rounded bg-gray-100 text-[11px] text-gray-700">{o.replace('https://', '')}</span>)}</div>
                </div>
              )}
            </Card>

            {site && (
              <Card className="p-4">
                <SectionTitle icon={Activity} right={<span className="text-[11px] text-gray-400">7 days</span>}>Live status</SectionTitle>
                <div className="grid grid-cols-2 gap-2">
                  <Stat icon={Globe} label="Sourced visits" value={fmtN(health.touches?.n)} sub={health.touches?.last_at ? `last ${fmtAgo(health.touches.last_at)}` : 'none yet'} />
                  <Stat icon={Zap} label="Events" value={fmtN(totalEvents)} sub={`${coverage}/${DL_EVENTS.length} e-commerce`} />
                  <Stat icon={ShoppingBag} label="Orders" value={fmtN(health.orders?.n)} sub={`${fmtN(health.orders?.with_source)} non-direct`} />
                  <Stat icon={Plug} label="Via plugin" value={fmtN(pluginN)} sub="server / page_data" tone={pluginN ? 'text-amber-700' : 'text-gray-900'} />
                </div>
                {(pluginN > 0 || site.has_wp_plugin || site.allow_keyless) && (
                  <div className="mt-3 space-y-2">
                    {purchasePluginOnly > 0 && (
                      <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-2.5 py-2 flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                        {fmtN(purchasePluginOnly)} of {fmtN(purchaseH.n)} purchases came only from the plugin. Turning the plugin off would lose these until GTM covers purchase.</p>
                    )}
                    {pluginN > 0 && (
                      <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-2 flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                        Before turning the WordPress plugin off, check Event coverage: these events must keep coming from dataLayer, Auto or a Glowy tag.</p>
                    )}
                    {site.allow_keyless && (
                      <p className="text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-2">Keyless requests are accepted (migration mode). Turn this off ~2 hours after the GTM publish.</p>
                    )}
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
